import { OverlayLoader } from 'components/Loaders'
import { actions, selectors } from 'data'
import { ReceiveMessageType } from 'data/types'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAuth from 'scenes/Auth/useAuth'
import { getMercuryGatewayWsUrl } from 'utils/urlBuilder'

import GeneralAlert from './GeneralAlert'
import { useInstruments } from './useInstruments'
import useWS from './useWS'

type Props = {
  children: React.ReactNode
}

// Bootstrap does currencies fetch, auth checks and WS connection in parallel
// to fasten feedback on UI
const Bootstrap = ({ children }: Props) => {
  const dispatch = useDispatch()
  useEffect(function BootstrapHook() {
    dispatch(actions.currencies.fetch())
    dispatch(actions.analytics.init())
    dispatch(actions.ticker.snapshot())

    const intervalId = setInterval(() => {
      dispatch(actions.ticker.snapshot())
    }, 5000)

    return () => clearInterval(intervalId)
  }, [])
  const authDone = useAuth()
  const instrumentsFetch = useInstruments()

  // TODO: Move mgw WS and market data WS to React context
  const {
    connecting: socketLoading,
    error: socketError,
    ready
  } = useWS(getMercuryGatewayWsUrl(), {
    onMessage: (message) => {
      const data = message as ReceiveMessageType
      dispatch(actions.socket.received(data))
    },
    onOpen: (ws) => {
      dispatch(actions.socket.setConnection(ws))
    }
  })

  const currencies = useSelector(selectors.currencies.getCurrencies)
  const errorLoadingCurrencies = useSelector(selectors.currencies.hasError)
  const loadingCurrencies = useSelector(selectors.currencies.isFetching)

  const appReady =
    authDone && currencies?.length > 0 && ready && instrumentsFetch.instruments.length > 0
  if (appReady) return <>{children}</>

  if (socketLoading || !authDone || loadingCurrencies || instrumentsFetch.loading)
    return <OverlayLoader />

  if (socketError) return <GeneralAlert code='WS-001' />

  if (errorLoadingCurrencies) return <GeneralAlert code='CURR-001' />

  if (instrumentsFetch.error) return <GeneralAlert code='INS-001' />

  // fallback, this shouldn't happen
  return <OverlayLoader />
}

export default Bootstrap
